/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import React, { useContext, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { SessionContext } from '../../../context/SessionContext';
import {
  TelemetryAction,
  TelemetryComponent,
  TelemetryState,
  useSendTelemetry,
} from '../../../hooks/useSendTelemetry';
import { Flex, Text } from '@getgo/chameleon-chakra-ui';
import { Link } from '../../../components/Link/Link';
import { isAndroid, isIos } from '../../../services/ClientService';
import { getHumanReadableMeetingId } from '../../../lib/meetingIdFormatter';
import { navigateTo } from '../../../services/NavigatorService';
import {
  buildMobileJoinScheme,
  getGoToAndroidAppUrl,
  getGoToIosAppUrl,
} from '../../../services/UrlService';

const messages = defineMessages({
  heading: {
    id: 'mobileJoinPage.heading',
  },
  install: {
    id: 'mobileJoinPage.install',
  },
  installLinkText: {
    id: 'mobileJoinPage.installLinkText',
  },
  join: {
    id: 'mobileJoinPage.join',
  },
  joinLinkText: {
    id: 'mobileJoinPage.joinLinkText',
  },
  meetingID: {
    id: 'mobileJoinPage.meetingID',
  },
});

export const MobileJoinPage: React.FC = () => {
  const { meetingId, sessionTrackingId } = useContext(SessionContext);
  const { formatMessage: f } = useIntl();
  const sendTelemetry = useSendTelemetry();
  const mobileLaunchScheme = buildMobileJoinScheme(
    meetingId,
    sessionTrackingId
  );
  const storeUrl = isAndroid ? getGoToAndroidAppUrl() : getGoToIosAppUrl();
  const lineHeight = 1.8;

  useEffect(() => {
    if (isAndroid) {
      sendTelemetry(
        TelemetryComponent.Android,
        TelemetryAction.Onload,
        TelemetryState.SecondManual
      );
      navigateTo(mobileLaunchScheme);
    } else if (isIos) {
      sendTelemetry(
        TelemetryComponent.Ios,
        TelemetryAction.Onload,
        TelemetryState.SecondManual
      );
    }
  });

  return (
    <Flex
      direction='column'
      id='errorStatus'
      m={10}
      justifyContent='space-between'
      w='100vw'
      alignItems='center'
      lineHeight={lineHeight}
    >
      <Flex direction='column'>
        <Text as='h2' color='white' textAlign='left'>
          {f(messages.heading)}
        </Text>
        <Flex mt='5vh' color='white' as='ol' direction='column' ml={4}>
          <li>
            <Text textAlign='left' pl='2vw'>
              {f(messages.install)}
              <br />
              <Link textDecoration='underline' href={storeUrl}>
                {f(messages.installLinkText)}
              </Link>
            </Text>
          </li>
          <li>
            <Text textAlign='left' mt='5vh' pl='2vw'>
              {f(messages.join)}
              <br />
              <Link
                textDecoration='underline'
                href={mobileLaunchScheme}
                onClick={() => {
                  if (isAndroid) {
                    sendTelemetry(
                      TelemetryComponent.Android,
                      TelemetryAction.Join,
                      TelemetryState.First
                    );
                  } else if (isIos) {
                    sendTelemetry(
                      TelemetryComponent.Ios,
                      TelemetryAction.Join,
                      TelemetryState.First
                    );
                  }
                }}
              >
                {f(messages.joinLinkText)}
              </Link>
            </Text>
          </li>
        </Flex>
      </Flex>
      <Flex my='10vw'>
        <Text fontSize='md' color='white'>
          {meetingId &&
            `${f(messages.meetingID)} ${getHumanReadableMeetingId(meetingId)}`}
        </Text>
      </Flex>
    </Flex>
  );
};
